
import { useState, useEffect } from 'react';
import { GridFilterItem, GridFilterModel, GridLogicOperator } from '@mui/x-data-grid-pro';
import { FilterOp, FilterParam, filtersEqual } from 'models/grids';
import { SetPreferenceSignature } from 'hooks/preferences';
import { IDashboardSettings } from 'components/preferences';

type FilterChangeHandler = (model: GridFilterModel) => void;

type UseGridFiltersProps = {
    settings?: IDashboardSettings | null,
    setSettings?: SetPreferenceSignature | null
};

type GridFiltersResult = [
    FilterParam,
    FilterChangeHandler,
    GridFilterModel
];

type UseGridFiltersSignature = (props: UseGridFiltersProps) => GridFiltersResult;

const useGridFilters: UseGridFiltersSignature = (props) => {
    const isCompleteGridFilter = (item: GridFilterItem) =>
        item.field && item.operator && item.value !== undefined && item.value !== null && item.value !== '';
    
    const isEqual = (a: GridFilterItem[], b: GridFilterItem[]) => {
        const aComplete = a.filter(isCompleteGridFilter);
        const bComplete = b.filter(isCompleteGridFilter);
    
        if (aComplete.length !== bComplete.length) return false;
        return aComplete.every((item, index) => {
            const bItem = bComplete[index];
            return (
                bItem &&
                item.field === bItem.field &&
                item.operator === bItem.operator &&
                item.value === bItem.value
            );
        });
    };
    const { settings, setSettings } = props;

    const [ value, setValue ] = useState<FilterParam>({
        logic: 'and',
        filters: []
    });    
    const [ filterModel, setFilterModel ] = useState<GridFilterModel>({
        items: []
    });

    const defaultFilter = settings?.defaultFilter;

    const handleFilterChange: FilterChangeHandler = (model) => {
        const operator = model.logicOperator == GridLogicOperator.Or && model.items.length > 1 ? 'or' : 'and';
    
        // Only include complete filters
        const completeItems = model.items.filter(isCompleteGridFilter);
        const filter: FilterParam = { filters: [], logic: operator };
    
        if (completeItems.length > 0) {
            completeItems.forEach((item) => {
                filter.filters.push({ field: item.field, operator: item.operator as FilterOp, value: item.value });
            });
        }
    
        // Only update state/settings if all filters are complete (or if there are no filters)
        if (completeItems.length === model.items.length || completeItems.length === 0) {
            if (settings && setSettings && (settings.filterParam == undefined || !filtersEqual(filter, settings.filterParam))) {
                setSettings({
                    ...settings,
                    filterParam: filter
                });
            }
    
            if (!filtersEqual(filter, value)) {
                setValue(filter);
            }
        }
    
        setFilterModel(model); // Always update the UI model so the panel stays in sync
    };

    useEffect(() => {        
        if (settings) {
            let newModel: GridFilterItem[] = [];
            
            newModel = settings?.filterParam?.filters.map(filter => {
                return {
                    field: filter?.field ?? '',
                    operator: filter?.operator ?? '',
                    value: filter?.value
                } as GridFilterItem
            }) ?? [];    
                        
            if (newModel && newModel.length == 0 && defaultFilter && defaultFilter !== undefined && defaultFilter !== null) {          
                newModel.push({ field: defaultFilter.field, operator: defaultFilter.operator as FilterOp, value: defaultFilter.value });
            };

            if (!isEqual(newModel, filterModel.items)) {
                setFilterModel({items: newModel, logicOperator: settings?.filterParam?.logic as GridLogicOperator ?? GridLogicOperator.And});
            }
            setValue({logic: settings.filterParam?.logic ?? 'and', filters: settings.filterParam?.filters ?? []})
        }
    }, [settings]);

    return [
        value,
        handleFilterChange,
        filterModel
    ];
};

export default useGridFilters;